import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  OutlinedInput,
  Tooltip,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const ROLE_LABELS = {
  manager: "מנהל",
  employee: "עובד",
};

const ROLE_COLORS = {
  manager: "#1976d2",
  employee: "#f57c00",
};

// ─── Dialog: create a new user for trade-delay ──────────────────────────────
const AddTradeDelayUserDialog = ({
  open,
  onClose,
  onAdd,
  managers,
  portalRoles,
}) => {
  const EMPTY = {
    Name: "",
    LastName: "",
    Email: "",
    PriorityUserName: "",
    tradeDelayRole: "",
    tradeDelayParentId: "",
  };
  const [form, setForm] = useState(EMPTY);
  const [emailError, setEmailError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (name === "Email") {
      setEmailError(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
    }
  };

  const handleRoleChange = (e) => {
    setForm((p) => ({
      ...p,
      tradeDelayRole: e.target.value,
      tradeDelayParentId: "",
    }));
  };

  const isValid =
    form.Name.trim() &&
    form.LastName.trim() &&
    form.Email.trim() &&
    form.PriorityUserName.trim() &&
    form.tradeDelayRole &&
    !emailError;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setServerError("");
    try {
      await onAdd({ ...form });
      setForm(EMPTY);
      setEmailError(false);
    } catch (err) {
      const msg = err?.data?.error || err?.msg || "שגיאה בהוספת משתמש";
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setForm(EMPTY);
    setEmailError(false);
    setServerError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: "#0054A5",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <PersonAddIcon />
        הוספת משתמש להיררכיית השהיות סחר
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="שם פרטי"
              name="Name"
              value={form.Name}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="שם משפחה"
              name="LastName"
              value={form.LastName}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="אימייל"
              name="Email"
              value={form.Email}
              onChange={handleChange}
              fullWidth
              required
              type="email"
              error={emailError}
              helperText={emailError ? "כתובת אימייל לא חוקית" : ""}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="שם משתמש Priority"
              name="PriorityUserName"
              value={form.PriorityUserName}
              onChange={handleChange}
              fullWidth
              required
              variant="outlined"
            />
          </Grid>

          {/* תפקיד בהיררכיה */}
          <Grid item xs={form.tradeDelayRole === "employee" ? 6 : 12}>
            <FormControl fullWidth required>
              <InputLabel>תפקיד</InputLabel>
              <Select
                value={form.tradeDelayRole}
                onChange={handleRoleChange}
                input={<OutlinedInput label="תפקיד" />}
              >
                <MenuItem value="manager">מנהל</MenuItem>
                <MenuItem value="employee">עובד</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {form.tradeDelayRole === "employee" && (
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>מנהל ממונה (אופציונלי)</InputLabel>
                <Select
                  value={form.tradeDelayParentId}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      tradeDelayParentId: e.target.value,
                    }))
                  }
                  input={<OutlinedInput label="מנהל ממונה (אופציונלי)" />}
                >
                  <MenuItem value="">ללא ממונה</MenuItem>
                  {managers.map((m) => (
                    <MenuItem key={m._id} value={m._id}>
                      {m.Name} {m.LastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {serverError && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography color="error" variant="body2">
            {serverError}
          </Typography>
        </Box>
      )}

      {portalRoles.length === 0 && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography color="warning.main" variant="caption">
            ⚠️ לא קיים תפקיד פורטל עם הרשאת השהיות סחר. יש ליצור תפקיד כזה תחילה
            בניהול הרשאות → קבוצות הרשאות, אחרת המשתמש לא יוכל לגשת לעמוד.
          </Typography>
        </Box>
      )}

      {serverError && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography color="error" variant="body2">
            {serverError}
          </Typography>
        </Box>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          ביטול
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || submitting}
          sx={{ bgcolor: "#0054A5" }}
        >
          {submitting ? "מוסיף..." : "הוסף משתמש"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Admin view: full table ──────────────────────────────────────────────────
const AdminHierarchyTable = ({ users, setUsers, execute, portalRoles }) => {
  const managers = users.filter((u) => u.tradeDelayRole === "manager");
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAddNewUser = async (formData) => {
    // automatically assign all portal roles with TradeDelayPage access
    const tradeDelayRoleIds = portalRoles.map((r) => r._id);
    try {
      const response = await execute("POST", `${BASE_URL}/api/users/adduser`, {
        Name: formData.Name,
        LastName: formData.LastName,
        Email: formData.Email,
        PriorityUserName: formData.PriorityUserName,
        Role: tradeDelayRoleIds,
        Branches: [],
        tradeDelayRole: formData.tradeDelayRole,
        tradeDelayParentId: formData.tradeDelayParentId || null,
      });
      const saved = response?.savedUser;
      if (saved) {
        setUsers((prev) => [
          ...prev,
          {
            ...saved,
            tradeDelayRole: formData.tradeDelayRole,
            tradeDelayParentId: formData.tradeDelayParentId || null,
            Role: portalRoles.map((r) => ({
              _id: r._id,
              Name: r.Name,
              TradeDelayPage: true,
            })),
          },
        ]);
      }
      setDialogOpen(false);
    } catch (err) {
      throw err; // let the dialog catch and display it
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    await execute("POST", `${BASE_URL}/api/users/updateTradeDelayRole`, {
      userId,
      tradeDelayRole: newRole || null,
      tradeDelayParentId: null,
    });
    setUsers((prev) =>
      prev.map((u) =>
        u._id === userId
          ? { ...u, tradeDelayRole: newRole || null, tradeDelayParentId: null }
          : u,
      ),
    );
  };

  const handleParentChange = async (userId, newParentId) => {
    await execute("POST", `${BASE_URL}/api/users/setTradeDelayParent`, {
      userId,
      tradeDelayParentId: newParentId || null,
    });
    setUsers((prev) =>
      prev.map((u) =>
        u._id === userId
          ? { ...u, tradeDelayParentId: newParentId || null }
          : u,
      ),
    );
  };

  const handleDeleteUser = async (userId) => {
    await execute("DELETE", `${BASE_URL}/api/users/deleteuser/${userId}`);
    setUsers((prev) => prev.filter((u) => u._id !== userId));
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#ebf7ff" }}>
            <TableCell sx={{ fontWeight: "bold" }}>שם</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>שם ב-Priority</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>הרשאת פורטל</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>תפקיד</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>מנהל ממונה</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u._id} hover>
              <TableCell>
                {u.Name} {u.LastName}
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {u.Email}
                </Typography>
              </TableCell>
              <TableCell>{u.PriorityUserName || "—"}</TableCell>
              <TableCell>
                {u.Role && u.Role.length > 0 ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {u.Role.map((r) => (
                      <Chip
                        key={r._id}
                        label={r.Name}
                        size="small"
                        color={r.TradeDelayPage ? "primary" : "default"}
                        variant={r.TradeDelayPage ? "filled" : "outlined"}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="error">
                    אין הרשאה
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Select
                  value={u.tradeDelayRole || "none"}
                  onChange={(e) =>
                    handleRoleChange(
                      u._id,
                      e.target.value === "none" ? null : e.target.value,
                    )
                  }
                  variant="standard"
                  size="small"
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="none">ללא תפקיד</MenuItem>
                  <MenuItem value="manager">מנהל</MenuItem>
                  <MenuItem value="employee">עובד</MenuItem>
                </Select>
              </TableCell>
              <TableCell>
                {u.tradeDelayRole === "employee" ? (
                  <Select
                    value={u.tradeDelayParentId || "none"}
                    onChange={(e) =>
                      handleParentChange(
                        u._id,
                        e.target.value === "none" ? null : e.target.value,
                      )
                    }
                    variant="standard"
                    size="small"
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="none">בחר מנהל...</MenuItem>
                    {managers
                      .filter((m) => m._id !== u._id)
                      .map((m) => (
                        <MenuItem key={m._id} value={m._id}>
                          {m.Name} {m.LastName}
                        </MenuItem>
                      ))}
                  </Select>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>
              <TableCell align="center">
                <Tooltip title="מחק משתמש מהמערכת">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteUser(u._id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          startIcon={<PersonAddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ borderColor: "#0054A5", color: "#0054A5" }}
        >
          הוסף משתמש
        </Button>
      </Box>

      <AddTradeDelayUserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onAdd={handleAddNewUser}
        managers={managers}
        portalRoles={portalRoles}
      />
    </TableContainer>
  );
};

// ─── Manager view: my employees + add ───────────────────────────────────────
const ManagerView = ({ currentUser, users, setUsers, execute }) => {
  const [selectedToAdd, setSelectedToAdd] = useState("");
  const [saving, setSaving] = useState(false);

  const myTeam = users.filter(
    (u) =>
      u.tradeDelayParentId &&
      u.tradeDelayParentId.toString() === currentUser._id.toString(),
  );

  const available = users.filter(
    (u) =>
      u.tradeDelayRole === "employee" &&
      !u.tradeDelayParentId &&
      u._id !== currentUser._id,
  );

  const handleAdd = async () => {
    if (!selectedToAdd) return;
    setSaving(true);
    try {
      await execute("POST", `${BASE_URL}/api/users/setTradeDelayParent`, {
        userId: selectedToAdd,
        tradeDelayParentId: currentUser._id,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u._id === selectedToAdd
            ? { ...u, tradeDelayParentId: currentUser._id }
            : u,
        ),
      );
      setSelectedToAdd("");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (userId) => {
    await execute("POST", `${BASE_URL}/api/users/setTradeDelayParent`, {
      userId,
      tradeDelayParentId: null,
    });
    setUsers((prev) =>
      prev.map((u) =>
        u._id === userId ? { ...u, tradeDelayParentId: null } : u,
      ),
    );
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        העובדים שלי
      </Typography>

      {myTeam.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          אין עובדים משויכים עדיין
        </Typography>
      ) : (
        <List dense disablePadding sx={{ mb: 2 }}>
          {myTeam.map((u) => (
            <ListItem
              key={u._id}
              sx={{
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 1,
                mb: 0.5,
                bgcolor: "#fafafa",
              }}
            >
              <ListItemText
                primary={`${u.Name} ${u.LastName}`}
                secondary={u.Email}
              />
              <Chip
                label={ROLE_LABELS[u.tradeDelayRole]}
                size="small"
                sx={{
                  bgcolor: ROLE_COLORS[u.tradeDelayRole] + "22",
                  color: ROLE_COLORS[u.tradeDelayRole],
                  fontWeight: 600,
                  mr: 1,
                }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  color="warning"
                  title="הסר מהצוות"
                  onClick={() => handleRemove(u._id)}
                >
                  <PersonRemoveIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        הוסף עובד לצוות
      </Typography>
      {available.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          אין עובדים זמינים לשיוך.
        </Typography>
      ) : (
        <Box display="flex" gap={1} alignItems="center">
          <Select
            value={selectedToAdd}
            onChange={(e) => setSelectedToAdd(e.target.value)}
            displayEmpty
            size="small"
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">בחר עובד להוסיף...</MenuItem>
            {available.map((u) => (
              <MenuItem key={u._id} value={u._id}>
                {u.Name} {u.LastName}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={handleAdd}
            disabled={!selectedToAdd || saving}
            sx={{ bgcolor: "#0054A5" }}
          >
            הוסף
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ─── Main export ─────────────────────────────────────────────────────────────
const TeamManagementTab = ({ currentUser, execute }) => {
  const [users, setUsers] = useState([]);
  const [portalRoles, setPortalRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = currentUser?.roles?.some((r) => r.admin);

  useEffect(() => {
    Promise.all([
      execute("GET", `${BASE_URL}/api/users/tradeDelayHierarchy`),
      execute("GET", `${BASE_URL}/api/roles/allroles`),
    ])
      .then(([usersRes, rolesRes]) => {
        const all = usersRes?.data || [];
        setUsers(all.filter((u) => u.tradeDelayRole));
        const allRoles = rolesRes?.data || rolesRes || [];
        setPortalRoles(
          allRoles.filter((r) => r.TradeDelayPage === true && !r.admin),
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <GroupsIcon sx={{ color: "#0054A5" }} />
        <Typography variant="h6" color="#0054A5">
          ניהול צוות
        </Typography>
      </Box>

      {isAdmin ? (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            ניהול היררכיית השהיות סחר — הגדרת תפקידים ושיוך עובדים למנהלים.
          </Typography>
          <AdminHierarchyTable
            users={users}
            setUsers={setUsers}
            execute={execute}
            portalRoles={portalRoles}
          />
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            שייך עובדים לצוות שלך.
          </Typography>
          <ManagerView
            currentUser={currentUser}
            users={users}
            setUsers={setUsers}
            execute={execute}
          />
        </>
      )}
    </Box>
  );
};

export default TeamManagementTab;
